// api/KanbanTaskApiClient.ts
import dayjs, { Dayjs } from 'dayjs';
import SkTechApiClient from "./base/SkTechApiClient";
import { KanbanTask, KanbanTaskComment, NewKanbanTask, NewKanbanTaskComment, KanbanTaskChange, KanbanTaskStatus } from '../stores/KanbanTaskStores';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { ExportTimeLogsParams } from '../stores/KanbanTaskStores';

dayjs.extend(localizedFormat);

export type Attachment = {
  publicId: string;
  url: string;
};

export type TaskTimeLog = {
  id: string;
  taskId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    photoId?: string;
  };
  startTime?: Dayjs;
  endTime: Dayjs;
  totalTime: string; // in format 'hh:mm:ss'
  description: string;
};

export type TaskTimeMetrics = {
  taskId: string;
  estimatedTime: string;
  timeSpent: string;
  remainingTime: string;
  isOvertime: boolean;
  overtimeAmount: string;
  percentComplete: number;
};

export type ScheduledAndPriorityUpdate = {
  startDate?: string;
  dueDate?: string;
  priority: number;
}

export default class KanbanTaskApiClient extends SkTechApiClient {
  //#region General methods for task
  public async getTasks(projectId: string): Promise<KanbanTask[]> {
    return this.Get(`/api/KanbanTask/project/${projectId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            // Parse createdDate, updatedDate
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public async getTaskById(taskId: string): Promise<KanbanTask | null> {
    return this.Get(`/api/KanbanTask/${taskId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async getDeletedTasks(projectId: string): Promise<KanbanTask[]> {
    return this.Get(`/api/KanbanTask/project/${projectId}/deleted`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public async getTaskDetails(taskIdOrPublicId: string, projectId?: string): Promise<KanbanTask | null> {
    return this.Get(`/api/KanbanTask/${taskIdOrPublicId} ${projectId ? `?projectId=${projectId}` : ''}`)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(`Error fetching task: ${taskIdOrPublicId}`, err);
        return null;
      });
  }

  public async createTask(task: NewKanbanTask): Promise<KanbanTask | null> {
    return this.Post('/api/KanbanTask', task)
      .then((response) => {
        // server returns 201 on success
        if (response?.status === 200 || response?.status === 201) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTask(taskId: string, task: KanbanTask): Promise<KanbanTask | null> {
    task.createdByUser = undefined;
    return this.Put(`/api/KanbanTask/${taskId}`, task)
      .then((response) => {
        // We now expect a 200 with the updated task in response.data
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async restoreTask(taskIdOrPublicId: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/restore/${taskIdOrPublicId}`, {})
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error("Error restoring task:", err);
        return null;
      });
  }

  async bulkRestore(taskIds: string[]) {
    return this.Patch(`/api/KanbanTask/restore/bulk`, {
      taskIds
    });
  }

  public async deleteTask(taskId: string): Promise<boolean> {
    return this.Delete(`/api/KanbanTask/${taskId}`)
      .then((response) => response?.status === 200 || response?.status === 204)
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  async bulkSoftDelete(taskIds: string[]) {
    return this.Delete(`/api/KanbanTask/bulk`, {
      taskIds,
      permanentDelete: false
    });
  }

  public async deleteTaskPermanent(taskId: string): Promise<boolean> {
    return this.Delete(`/api/KanbanTask/permanent/${taskId}`)
      .then((response) => response?.status === 200 || response?.status === 204)
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  async bulkPermanentDelete(taskIds: string[]) {
    return this.Delete(`/api/KanbanTask/permanent/bulk`, {
      taskIds,
      permanentDelete: true
    });
  }
  //#endregion



  //#region Task Time Methods
  public async getTaskTimeMetrics(taskId: string): Promise<TaskTimeMetrics | null> {
    return this.Get(`/api/KanbanTask/${taskId}/time`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error fetching task time metrics:', err);
        return null;
      });
  }

  public async updateEstimatedTime(taskId: string, estimatedTimeStr: string): Promise<TaskTimeMetrics | null> {
    return this.Post(`/api/KanbanTask/${taskId}/estimated-time`, { estimatedTimeStr })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error updating estimated time:', err);
        return null;
      });
  }

  public async logTimeForTask(taskId: string, time: string, description: string, userId?: string, endDate?: string): Promise<any> {
    return this.Post(`/api/KanbanTask/${taskId}/log-time`, {
      time,
      description,
      userId,
      endDate: endDate ? endDate : undefined
    })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error logging time for task:', err);
        return null;
      });
  }

  public async getTaskTimeLogs(taskId: string): Promise<TaskTimeLog[]> {
    return this.Get(`/api/KanbanTask/${taskId}/time-logs`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((log: any) => ({
            ...log,
            startTime: log.startTime ? dayjs.utc(log.startTime).local() : undefined,
            endTime: dayjs.utc(log.endTime).local()
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching task time logs:', err);
        return [];
      });
  }
  //#endregion



  //#region Move Task API Methods
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    await this.Post(`/api/KanbanTask/${taskId}/move`, {
      toStatusId: Number(toStatusId),
      toIndex
    });
  }

  public async updateTaskOrder(taskId: string, newIndex: number): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/order`, { newIndex })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Change History
  public async getTaskChanges(taskId: string): Promise<KanbanTaskChange[]> {
    return this.Get(`/api/KanbanTask/${taskId}/changes`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((item: any) => ({
            ...item,
            createdDate: dayjs(item.createdDate),
          })) as KanbanTaskChange[];
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }
  //#endregion



  //#region Custom Field API Methods
  public async getProjectCustomFields(projectId: string): Promise<any[]> {
    return this.Get(`/api/CustomField/project/${projectId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching project custom fields:', err);
        return [];
      });
  }

  public async getTaskCustomFieldValues(taskId: string): Promise<any[]> {
    return this.Get(`/api/CustomFieldValue/task/${taskId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching task custom field values:', err);
        return [];
      });
  }

  public async setTaskCustomFieldValue(taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> {
    return this.Put(`/api/CustomFieldValue/task/${taskId}/field/${fieldId}`, { value, userId })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error setting custom field value:', err);
        return false;
      });
  }

  public async updateTaskCustomFieldValues(taskId: string, fieldValues: Record<string, string>): Promise<boolean> {
    return this.Put(`/api/CustomFieldValue/task/${taskId}/batch`, { fieldValues })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error updating task custom field values:', err);
        return false;
      });
  }

  public async createCustomField(field: any): Promise<any> {
    return this.Post('/api/CustomField', field)
      .then((response) => {
        if (response?.status === 201) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error creating custom field:', err);
        return null;
      });
  }

  public async updateCustomField(fieldId: string, field: any): Promise<any> {
    return this.Put(`/api/CustomField/${fieldId}`, field)
      .then((response) => {
        if (response?.status === 204) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error updating custom field:', err);
        return null;
      });
  }

  public async deleteCustomField(fieldId: string): Promise<boolean> {
    return this.Delete(`/api/CustomField/${fieldId}`)
      .then((response) => {
        return response?.status === 204;
      })
      .catch((err) => {
        console.error('Error deleting custom field:', err);
        return false;
      });
  }

  public async updateCustomFieldOrder(fieldId: string, newOrder: number): Promise<boolean> {
    return this.Patch(`/api/CustomField/${fieldId}/order`, { newOrder })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error updating custom field order:', err);
        return false;
      });
  }
  //#endregion



  //#region Attachment API Methods
  public async uploadTaskAttachment(taskId: string, file: File, confirmation: boolean = false): Promise<Attachment | null> {
    try {
      // First, get the task details to get the project ID
      const task = await this.getTaskDetails(taskId);
      if (!task) {
        console.error(`Could not find task with ID ${taskId}`);
        return null;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("taskId", taskId);
      formData.append("projectId", task.projectId.toString());

      let url = `/api/KanbanTask/UploadKanbanTaskAttachment`;
      if (confirmation) {
        url += `?confirmation=true`;
      }

      console.log(`Uploading file ${file.name} (${file.size} bytes) to task ${taskId} in project ${task.projectId}`);

      return this.Post(url, formData)
        .then((response: any) => {
          if (response?.status === 200 && response.data) {
            console.log('Upload successful:', response.data);
            return {
              publicId: response.data.publicId,
              url: response.data.secureUrl || response.data.url
            };
          }
          console.error('Upload failed, response:', response);
          return null;
        });
    } catch (err) {
      console.error('Error in uploadTaskAttachment:', err);
      throw err;
    }
  }

  public async getTaskAttachments(taskId: string): Promise<Attachment[]> {
    try {
      // First, get the task details to get the project ID
      const task = await this.getTaskDetails(taskId);
      if (!task) {
        console.error(`Could not find task with ID ${taskId}`);
        return [];
      }

      console.log(`Fetching attachments for task ${taskId} in project ${task.projectId}`);

      return this.Get(`/api/File/GetKanbanTaskAttachments?taskId=${taskId}&projectId=${task.projectId}`)
        .then((response) => {
          if (response?.status === 200 && Array.isArray(response.data)) {
            console.log(`Retrieved ${response.data.length} attachments`);

            const attachments = response.data.map((item: any) => ({
              publicId: item.publicId,
              url: item.url,
              fileName: item.fileName || item.publicId.split('/').pop(),
              size: item.size,
              createdAt: item.createdAt
            }));

            return attachments;
          }
          console.warn('No attachments found or unexpected response format:', response);
          return [];
        });
    } catch (err) {
      console.error('Error in getTaskAttachments:', err);
      return [];
    }
  }

  public async deleteFile(publicId: string): Promise<boolean> {
    try {
      console.log(`Deleting file with publicId: ${publicId}`);

      return this.Delete(`/api/File/${encodeURIComponent(publicId)}`)
        .then((response) => {
          const success = response?.status === 200 || response?.status === 204;
          console.log(`Delete ${success ? 'successful' : 'failed'}:`, response);
          return success;
        });
    } catch (err) {
      console.error('Error in deleteFile:', err);
      return false;
    }
  }
  //#endregion



  //#region Task Status Update
  public async updateTaskStatus(taskId: string, statusId: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/status`, { statusId: statusId })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Comments API Methods
  public getTaskComments = async (taskId: string): Promise<KanbanTaskComment[]> => {
    return this.Get(`/api/KanbanTask/${taskId}/comments`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((comment: any) => ({
            ...comment,
            // Use createdDate for comments
            createdDate: dayjs(comment.createdDate),
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  };

  public async createComment(comment: NewKanbanTaskComment): Promise<KanbanTaskComment | null> {
    return this.Post(`/api/KanbanTask/${comment.taskId}/comments`, comment)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public deleteComment = async (commentId: string): Promise<boolean> => {
    return await this.Delete(`/api/KanbanTask/comments/${commentId}`)
      .then(response => response?.status === 200);
  }
  //#endregion



  //#region Individual Field Updates
  public async updateTaskTitle(taskId: string, title: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/title`, { title: title })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskDescription(taskId: string, description: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/description`, { description: description })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskScheduledAndPriority(taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/scheduledAndPriority`, scheduledAndPriority)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskAssignee(taskId: string, assigneeId?: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/KanbanTask/${taskId}/assignee/${assigneeId}`)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Status API Methods
  public async getProjectStatuses(projectId: string): Promise<KanbanTaskStatus[]> {
    return this.Get(`/api/KanbanTaskStatus/project/${projectId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public async createStatus(name: string, projectId: string, index: number, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> {
    const status = {
      name,
      projectId: Number(projectId),
      index,
      isFinalStatus
    };

    return this.Post('/api/KanbanTaskStatus', status)
      .then((response) => {
        if (response?.status === 201) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateStatus(statusId: string, status: KanbanTaskStatus): Promise<KanbanTaskStatus | null> {
    return this.Put(`/api/KanbanTaskStatus/${statusId}`, status)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async deleteStatus(statusId: string): Promise<boolean> {
    return this.Delete(`/api/KanbanTaskStatus/${statusId}`)
      .then((response) => {
        return response?.status === 204;
      })
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  public async updateStatusOrder(statusId: string, newIndex: number): Promise<KanbanTaskStatus | null> {
    return this.Patch(`/api/KanbanTaskStatus/${statusId}/order`, { newIndex })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Export Time Logs API Methods
  public async exportProjectTimeLogs(params: ExportTimeLogsParams):
    Promise<{ blob: Blob; headers: any } | null> {
    return this.Post('/api/KanbanTask/ExportProjectTimeLogs', params, { responseType: 'blob' })
      .then((response: any) => {
        if (response?.status >= 200 && response?.status < 300) {
          return { blob: response.data as Blob, headers: response.headers };
        }
        return null;
      })
      .catch((err) => {
        console.error('exportProjectTimeLogs error:', err);
        return null;
      });
  }
  //#endregion
}